import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, MapPin, Shield, Languages as LangIcon, Edit2, Loader2, Check, Crown, X, Briefcase, GraduationCap, Ruler, Heart as HeartIcon, Wine, Cigarette, Baby, Church } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import PremiumModal from '../components/PremiumModal';
import PhotoGrid from '../components/PhotoGrid';
import PhotoCarousel from '../components/PhotoCarousel';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import API_URL from '../config/api';
import { localizeCity } from '../utils/cityLocale';
import SEOHead from '../components/SEOHead';

const COVER_IMAGE = 'https://images.unsplash.com/photo-1765101160082-506d55b8c2af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxQb3J0dWdhbCUyMGxhbmRzY2FwZSUyMHNjZW5pYyUyMGdvbGRlbiUyMGhvdXIlMjByb21hbnRpY3xlbnwwfHx8fDE3NzU3NDAzOTh8MA&ixlib=rb-4.1.0&q=85&w=1200';

const COUNTRIES = {
  PT: 'Portugal', FR: 'France', CH: 'Switzerland', LU: 'Luxembourg',
  BE: 'Belgium', DE: 'Germany', ES: 'Spain',
};
const COUNTRY_LIST = [
  { code: 'PT', name: 'Portugal' }, { code: 'FR', name: 'France' },
  { code: 'CH', name: 'Switzerland' }, { code: 'LU', name: 'Luxembourg' },
  { code: 'BE', name: 'Belgium' }, { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
];

const INTENTIONS_MAP = {
  serious: 'Serious Relationship', casual: 'Taking it Slow',
  friendship: 'Friendship First', not_sure: 'Not Sure Yet',
};
const INTENTION_OPTIONS = [
  { value: 'serious', label: 'Serious Relationship' },
  { value: 'casual', label: 'Taking it Slow' },
  { value: 'friendship', label: 'Friendship First' },
  { value: 'not_sure', label: 'Not Sure Yet' },
];
const LOOKING_FOR_OPTIONS = [
  { value: 'male', label: 'Men' },
  { value: 'female', label: 'Women' },
  { value: 'everyone', label: 'Everyone' },
];

const EDUCATION_LABELS = {
  high_school: 'High School', bachelors: "Bachelor's Degree",
  masters: "Master's Degree", doctorate: 'Doctorate / PhD', other: 'Other',
};
const EDUCATION_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'high_school', label: 'High School' },
  { value: 'bachelors', label: "Bachelor's Degree" },
  { value: 'masters', label: "Master's Degree" },
  { value: 'doctorate', label: 'Doctorate / PhD' },
  { value: 'other', label: 'Other' },
];
const RELIGION_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'catholic', label: 'Catholic' }, { value: 'christian', label: 'Christian' },
  { value: 'muslim', label: 'Muslim' }, { value: 'jewish', label: 'Jewish' },
  { value: 'buddhist', label: 'Buddhist' }, { value: 'agnostic', label: 'Agnostic' },
  { value: 'atheist', label: 'Atheist' }, { value: 'spiritual', label: 'Spiritual' },
  { value: 'other', label: 'Other' },
];
const LANGUAGES = ['Portuguese', 'English', 'French', 'German', 'Spanish', 'Italian', 'Dutch'];

export default function Profile() {
  const { userId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').substring(0, 2);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPremium, setShowPremium] = useState(false);
  const [userPhotos, setUserPhotos] = useState([]);

  const isOwnProfile = !userId || userId === currentUser?.user_id;

  const fetchPhotos = useCallback(async () => {
    if (!isOwnProfile) return;
    try {
      const res = await axios.get(`${API_URL}/api/users/me/photos`, { withCredentials: true });
      setUserPhotos(res.data.photos || []);
    } catch (err) {
      // Non-critical: photo grid will render empty if fetch fails; profile still loads
      setUserPhotos([]);
    }
  }, [isOwnProfile]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      if (isOwnProfile) {
        const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
        setProfile(response.data);
        setEditData(response.data);
      } else {
        const response = await axios.get(`${API_URL}/api/profile/${userId}`, { withCredentials: true });
        setProfile(response.data);
      }
    } catch (err) {
      toast.error(t('profile.profile_load_failed'));
      if (!isOwnProfile) navigate('/discover');
    } finally {
      setLoading(false);
    }
  }, [isOwnProfile, userId, navigate]);

  useEffect(() => {
    fetchProfile();
    fetchPhotos();
  }, [fetchProfile, fetchPhotos]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...editData };
      // Remove read-only / system fields
      const removeKeys = ['user_id','email','role','created_at','updated_at','password_hash','is_email_verified','onboarding_complete','photos','current_plan','plan_features','subscription','google_picture','auth_provider','is_verified','verification_photo_url','verified_at','boost','name','birthdate'];
      removeKeys.forEach(k => delete payload[k]);
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] === null || payload[k] === undefined) delete payload[k];
      });

      await axios.put(`${API_URL}/api/profile`, payload, { withCredentials: true });
      await refreshUser();
      await fetchProfile();
      setEditing(false);
      toast.success(t('profile.profile_updated'));
    } catch (err) {
      toast.error(t('profile.profile_update_failed'));
    } finally {
      setSaving(false);
    }
  };

  const getPhotoUrl = (p) => {
    const photos = p?.photos || [];
    if (photos.length > 0) return `${API_URL}/api/photos/${photos[0]}?v=${Date.now()}`;
    if (p?.google_picture) return p.google_picture;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(p?.name || 'U')}&background=6E1A24&color=fff&size=400`;
  };

  const getAge = (birthdate) => {
    if (!birthdate) return null;
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const updateEdit = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

  const toggleLanguage = (lang) => {
    const current = editData.spoken_languages || [];
    updateEdit('spoken_languages',
      current.includes(lang) ? current.filter(l => l !== lang) : [...current, lang]
    );
  };

  const cancelEdit = () => {
    setEditData(profile);
    fetchPhotos();
    setEditing(false);
  };

  const boolLabel = (val) => val ? (val === 'yes' ? t('profile.yes') : t('profile.no')) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#6E1A24] animate-spin" />
      </div>
    );
  }

  const plan = profile?.current_plan || profile?.subscription?.plan || 'free';

  // Build photo objects for carousel (other user's profile or own view mode)
  const profilePhotoObjects = (profile?.photos || []).map(pid => ({ photo_id: pid }));

  const DetailCard = ({ icon: Icon, label, value }) => {
    if (!value) return null;
    return (
      <div className="flex items-center gap-3 bg-[#F5F2EB] rounded-xl p-3">
        <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
          <Icon className="w-4 h-4 text-[#6E1A24]" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-[#9C9C97] leading-none mb-0.5">{label}</p>
          <p className="text-sm font-medium text-[#2D2D2A] truncate">{value}</p>
        </div>
      </div>
    );
  };

  const EditField = ({ label, children }) => (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-1.5">{label}</label>
      {children}
    </div>
  );
  const inputCls = 'w-full px-3.5 py-2.5 bg-white border border-[#E5E3DB] rounded-xl text-sm text-[#2D2D2A] placeholder-[#9C9C97] focus:outline-none focus:border-[#6E1A24] focus:ring-2 focus:ring-[#6E1A24]/10 transition-all';
  const selectCls = inputCls + ' appearance-none';
  const toggleBtnCls = (active) =>
    `flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${active ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="profile-page">
      <SEOHead title="Profile" noindex path="/profile" />
      {isOwnProfile && <Navigation />}

      {/* Portuguese Landscape Cover */}
      <div className={`relative ${isOwnProfile ? 'h-44 sm:h-52' : 'h-40 sm:h-48'} ${isOwnProfile ? 'mt-16' : ''}`}>
        <img src={COVER_IMAGE} alt="Portuguese landscape" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#FAF9F6]" />
        {!isOwnProfile && (
          <button onClick={() => navigate(-1)} className="absolute top-4 left-4 w-10 h-10 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/40 transition-colors" data-testid="back-btn">
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Profile Card */}
      <div className="px-4 sm:px-6 -mt-16 relative z-10 pb-28">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-3xl shadow-lg shadow-black/5 border border-[#E5E3DB]/60 overflow-hidden">

            {/* Header: Photo + Identity */}
            <div className="p-5 sm:p-7">
              <div className="flex flex-col sm:flex-row gap-5 sm:gap-7">
                {/* Portrait Photo / Carousel */}
                <div className="relative flex-shrink-0 w-full sm:w-40 max-w-[200px] mx-auto sm:mx-0">
                  {editing ? (
                    /* In edit mode, show a small preview of main photo */
                    <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-[#F5F2EB] shadow-md ring-2 ring-white">
                      <img src={userPhotos.length > 0 ? `${API_URL}/api/photos/${userPhotos[0].photo_id}?v=${Date.now()}` : getPhotoUrl(profile)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <PhotoCarousel
                      photos={isOwnProfile ? userPhotos : profilePhotoObjects}
                      fallbackUrl={getPhotoUrl(profile)}
                      className="aspect-[3/4] rounded-2xl shadow-md ring-2 ring-white"
                    />
                  )}
                </div>

                {/* Identity & Actions */}
                <div className="flex-1 min-w-0 pt-1 text-center sm:text-left">
                  <h1 className="text-2xl sm:text-3xl font-serif font-medium text-[#2D2D2A] leading-tight" data-testid="profile-name">
                    {profile?.nickname || profile?.name}
                    {profile?.birthdate && (
                      <span className="font-sans font-light text-[#6C6C67] ml-1.5">{getAge(profile.birthdate)}</span>
                    )}
                  </h1>

                  <div className="flex items-center gap-1.5 text-[#6C6C67] mt-1.5 justify-center sm:justify-start" data-testid="profile-location">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    <span className="text-sm truncate">
                      {localizeCity(profile?.city, lang)}{profile?.country ? `, ${COUNTRIES[profile.country] || profile.country}` : ''}
                    </span>
                  </div>

                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 mt-3 justify-center sm:justify-start">
                    {profile?.is_verified && (
                      <span className="inline-flex items-center gap-1 bg-[#2D6A4F]/10 text-[#2D6A4F] text-xs font-semibold rounded-full px-2.5 py-1" data-testid="verified-badge">
                        <Shield className="w-3.5 h-3.5" /> Verified
                      </span>
                    )}
                    {plan === 'gold' && (
                      <span className="inline-flex items-center gap-1 bg-[#D4AF37]/15 text-[#96791D] text-xs font-semibold rounded-full px-2.5 py-1" data-testid="gold-badge">
                        <Crown className="w-3.5 h-3.5" /> Gold
                      </span>
                    )}
                    {plan === 'premium' && (
                      <span className="inline-flex items-center gap-1 bg-[#6E1A24]/10 text-[#6E1A24] text-xs font-semibold rounded-full px-2.5 py-1" data-testid="premium-badge">
                        <Crown className="w-3.5 h-3.5" /> Premium
                      </span>
                    )}
                  </div>

                  {/* Edit / Save / Cancel */}
                  {isOwnProfile && (
                    <div className="mt-4 flex gap-2 justify-center sm:justify-start">
                      {editing ? (
                        <>
                          <Button onClick={handleSave} disabled={saving} className="bg-[#6E1A24] text-white rounded-xl px-5 py-2 text-sm font-semibold hover:bg-[#8A2331] transition-all flex items-center gap-1.5" data-testid="save-profile">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t('profile.save_changes')}
                          </Button>
                          <Button onClick={cancelEdit} variant="outline" className="rounded-xl px-4 py-2 text-sm border-[#E5E3DB] text-[#6C6C67] hover:bg-[#F5F2EB]" data-testid="cancel-edit">
                            <X className="w-4 h-4 mr-1" /> {t('common.cancel')}
                          </Button>
                        </>
                      ) : (
                        <Button onClick={() => setEditing(true)} className="bg-[#F5F2EB] text-[#2D2D2A] rounded-xl px-5 py-2 text-sm font-medium hover:bg-[#E5E3DB] transition-all flex items-center gap-1.5" data-testid="edit-profile">
                          <Edit2 className="w-4 h-4" /> {t('profile.edit_profile')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="h-px bg-[#E5E3DB]" />

            {/* Content */}
            <div className="p-5 sm:p-7 space-y-7">

              {/* Photo Management (edit mode only) */}
              {editing && isOwnProfile && (
                <section data-testid="photo-management">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">{t('profile.your_photos')}</h3>
                  <PhotoGrid photos={userPhotos} onPhotosChange={setUserPhotos} />
                </section>
              )}

              {/* About */}
              <section data-testid="profile-about">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-2">{t('profile.about')}</h3>
                {editing ? (
                  <textarea value={editData.bio || ''} onChange={(e) => updateEdit('bio', e.target.value)} className={inputCls + ' resize-none h-28'} placeholder={t('profile.bio_placeholder')} data-testid="edit-bio" />
                ) : (
                  <p className="text-[#2D2D2A] text-sm leading-relaxed whitespace-pre-line">
                    {profile?.bio || <span className="text-[#9C9C97] italic">{t('profile.no_bio')}</span>}
                  </p>
                )}
              </section>

              {/* Location (edit mode) */}
              {editing && (
                <section className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="edit-location">
                  <EditField label={t('profile.city_label')}>
                    <input type="text" value={editData.city || ''} onChange={(e) => updateEdit('city', e.target.value)} className={inputCls} placeholder={t('profile.city_placeholder')} data-testid="edit-city" />
                  </EditField>
                  <EditField label={t('profile.country_label')}>
                    <select value={editData.country || ''} onChange={(e) => updateEdit('country', e.target.value)} className={selectCls} data-testid="edit-country">
                      <option value="">{t('profile.select_placeholder')}</option>
                      {COUNTRY_LIST.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </EditField>
                </section>
              )}

              {/* Looking For */}
              <section data-testid="profile-intentions">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-2">{t('profile.looking_for')}</h3>
                {editing ? (
                  <div className="space-y-4">
                    <EditField label={t('profile.interested_in')}>
                      <div className="flex gap-2">
                        {LOOKING_FOR_OPTIONS.map(opt => (
                          <button key={opt.value} onClick={() => updateEdit('looking_for', opt.value)} className={toggleBtnCls(editData.looking_for === opt.value)} data-testid={`edit-looking-${opt.value}`}>{opt.value === 'male' ? t('profile.men') : opt.value === 'female' ? t('profile.women') : t('profile.everyone')}</button>
                        ))}
                      </div>
                    </EditField>
                    <EditField label={t('profile.intention')}>
                      <div className="grid grid-cols-2 gap-2">
                        {INTENTION_OPTIONS.map(opt => (
                          <button key={opt.value} onClick={() => updateEdit('relationship_intention', opt.value)} className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-all text-left ${editData.relationship_intention === opt.value ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`edit-intention-${opt.value}`}>{t(`profile.${opt.value}`)}</button>
                        ))}
                      </div>
                    </EditField>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profile?.looking_for && (
                      <span className="bg-[#6E1A24]/8 text-[#6E1A24] text-sm font-medium px-3 py-1.5 rounded-full">
                        {profile.looking_for === 'male' ? t('profile.men') : profile.looking_for === 'female' ? t('profile.women') : t('profile.everyone')}
                      </span>
                    )}
                    {profile?.relationship_intention && (
                      <span className="bg-[#F5F2EB] text-[#2D2D2A] text-sm font-medium px-3 py-1.5 rounded-full">
                        {t(`profile.${profile.relationship_intention}`)}
                      </span>
                    )}
                    {!profile?.looking_for && !profile?.relationship_intention && (
                      <span className="text-sm text-[#9C9C97] italic">{t('profile.not_specified')}</span>
                    )}
                  </div>
                )}
              </section>

              {/* Languages */}
              <section data-testid="profile-languages">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-2 flex items-center gap-1.5">
                  <LangIcon className="w-3.5 h-3.5" /> {t('profile.languages')}
                </h3>
                {editing ? (
                  <div className="flex flex-wrap gap-2">
                    {LANGUAGES.map(lang => {
                      const active = (editData.spoken_languages || []).includes(lang);
                      return (
                        <button key={lang} onClick={() => toggleLanguage(lang)} className={`py-2 px-4 rounded-full border text-sm font-medium transition-all ${active ? 'bg-[#6E1A24] text-white border-[#6E1A24]' : 'bg-white text-[#6C6C67] border-[#E5E3DB] hover:border-[#6E1A24]/30'}`} data-testid={`edit-lang-${lang}`}>{lang}</button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {profile?.spoken_languages?.length > 0 ? profile.spoken_languages.map(lang => (
                      <span key={lang} className="bg-[#F5F2EB] text-[#2D2D2A] text-sm px-3 py-1.5 rounded-full">{lang}</span>
                    )) : <span className="text-sm text-[#9C9C97] italic">{t('profile.not_specified')}</span>}
                  </div>
                )}
              </section>

              {/* Details */}
              <section data-testid="profile-details">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-[#9C9C97] mb-3">{t('profile.details')}</h3>
                {editing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <EditField label={t('profile.occupation')}>
                      <input type="text" value={editData.occupation || ''} onChange={(e) => updateEdit('occupation', e.target.value)} className={inputCls} placeholder={t('profile.occupation_placeholder')} data-testid="edit-occupation" />
                    </EditField>
                    <EditField label={t('profile.education')}>
                      <select value={editData.education || ''} onChange={(e) => updateEdit('education', e.target.value)} className={selectCls} data-testid="edit-education">
                        {EDUCATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </EditField>
                    <EditField label={t('profile.height_label')}>
                      <input type="number" min="100" max="250" value={editData.height || ''} onChange={(e) => updateEdit('height', e.target.value ? parseInt(e.target.value) : '')} className={inputCls} placeholder={t('profile.height_placeholder')} data-testid="edit-height" />
                    </EditField>
                    <EditField label={t('profile.religion')}>
                      <select value={editData.religion || ''} onChange={(e) => updateEdit('religion', e.target.value)} className={selectCls} data-testid="edit-religion">
                        {RELIGION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </EditField>
                    <EditField label={t('profile.smoking')}>
                      <div className="flex gap-2">
                        {['yes', 'no'].map(v => (
                          <button key={v} onClick={() => updateEdit('smoking', editData.smoking === v ? '' : v)} className={toggleBtnCls(editData.smoking === v)} data-testid={`edit-smoking-${v}`}>{v === 'yes' ? t('profile.yes') : t('profile.no')}</button>
                        ))}
                      </div>
                    </EditField>
                    <EditField label={t('profile.drinking')}>
                      <div className="flex gap-2">
                        {['yes', 'no'].map(v => (
                          <button key={v} onClick={() => updateEdit('drinking', editData.drinking === v ? '' : v)} className={toggleBtnCls(editData.drinking === v)} data-testid={`edit-drinking-${v}`}>{v === 'yes' ? t('profile.yes') : t('profile.no')}</button>
                        ))}
                      </div>
                    </EditField>
                    <EditField label={t('profile.has_children')}>
                      <div className="flex gap-2">
                        {['yes', 'no'].map(v => (
                          <button key={v} onClick={() => updateEdit('has_children', editData.has_children === v ? '' : v)} className={toggleBtnCls(editData.has_children === v)} data-testid={`edit-has-children-${v}`}>{v === 'yes' ? t('profile.yes') : t('profile.no')}</button>
                        ))}
                      </div>
                    </EditField>
                    <EditField label={t('profile.wants_children')}>
                      <div className="flex gap-2">
                        {['yes', 'no'].map(v => (
                          <button key={v} onClick={() => updateEdit('wants_children', editData.wants_children === v ? '' : v)} className={toggleBtnCls(editData.wants_children === v)} data-testid={`edit-wants-children-${v}`}>{v === 'yes' ? t('profile.yes') : t('profile.no')}</button>
                        ))}
                      </div>
                    </EditField>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <DetailCard icon={Briefcase} label={t('profile.occupation')} value={profile?.occupation} />
                    <DetailCard icon={GraduationCap} label={t('profile.education')} value={profile?.education ? (EDUCATION_LABELS[profile.education] || profile.education) : null} />
                    <DetailCard icon={Ruler} label={t('profile.height')} value={profile?.height ? `${profile.height} cm` : null} />
                    <DetailCard icon={Church} label={t('profile.religion')} value={profile?.religion ? profile.religion.charAt(0).toUpperCase() + profile.religion.slice(1) : null} />
                    <DetailCard icon={Cigarette} label={t('profile.smoking')} value={profile?.smoking ? (profile.smoking === 'yes' ? t('profile.yes') : t('profile.no')) : null} />
                    <DetailCard icon={Wine} label={t('profile.drinking')} value={profile?.drinking ? (profile.drinking === 'yes' ? t('profile.yes') : t('profile.no')) : null} />
                    <DetailCard icon={Baby} label={t('profile.children')} value={profile?.has_children ? (profile.has_children === 'yes' ? t('profile.yes') : t('profile.no')) : null} />
                    <DetailCard icon={HeartIcon} label={t('profile.wants_children')} value={profile?.wants_children ? (profile.wants_children === 'yes' ? t('profile.yes') : t('profile.no')) : null} />
                    {!profile?.occupation && !profile?.education && !profile?.height && !profile?.religion && !profile?.smoking && !profile?.drinking && !profile?.has_children && !profile?.wants_children && (
                      <p className="col-span-1 sm:col-span-2 text-sm text-[#9C9C97] italic">{t('profile.no_details')}</p>
                    )}
                  </div>
                )}
              </section>

              {/* Member Since */}
              <div className="text-center pt-2" data-testid="member-since">
                <p className="text-xs text-[#9C9C97]">
                  {t('common.member_since', { date: profile?.created_at ? format(new Date(profile.created_at), 'MMMM yyyy') : '\u2014' })}
                </p>
              </div>

              {/* Subscription */}
              {isOwnProfile && (
                <div className="pt-4 border-t border-[#E5E3DB]">
                  {plan === 'gold' ? (
                    <div className="flex items-center gap-3 p-4 rounded-2xl bg-gradient-to-r from-[#D4AF37]/5 to-[#D4AF37]/10 border border-[#D4AF37]/20" data-testid="profile-gold-badge">
                      <div className="w-10 h-10 rounded-xl bg-[#D4AF37] flex items-center justify-center">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-[#2D2D2A] text-sm">{t('profile.gold_status')}</p>
                        <p className="text-xs text-[#6C6C67]">{t('profile.premium_features_active')}</p>
                      </div>
                    </div>
                  ) : plan === 'premium' ? (
                    <Link to="/checkout?plan=gold" className="block w-full p-4 rounded-2xl bg-gradient-to-r from-[#D4AF37]/5 to-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center gap-4 hover:shadow-md transition-all group" data-testid="profile-go-gold-btn">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-[#2D2D2A] text-sm">{t('profile.upgrade_go_gold')}</p>
                        <p className="text-xs text-[#6C6C67]">{t('profile.upgrade_go_gold_desc')}</p>
                      </div>
                    </Link>
                  ) : (
                    <Link to="/pricing" className="block w-full p-4 rounded-2xl bg-gradient-to-r from-[#6E1A24]/5 to-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center gap-4 hover:shadow-md transition-all group" data-testid="profile-upgrade-btn">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#C97A63] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <Crown className="w-5 h-5 text-white" />
                      </div>
                      <div className="text-left flex-1">
                        <p className="font-medium text-[#2D2D2A] text-sm">{t('profile.upgrade_cta_free')}</p>
                        <p className="text-xs text-[#6C6C67]">{t('profile.upgrade_cta_free_desc')}</p>
                      </div>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <PremiumModal
        isOpen={showPremium}
        onClose={() => setShowPremium(false)}
        onUpgradeSuccess={async () => { await refreshUser(); await fetchProfile(); }}
        currentPlan={plan}
      />
    </div>
  );
}
